import {buildTourProgressElement, postJson} from './product-tours.js'

export const TWO_FACTOR_SETUP_TOUR_EVENT = 'cpm:tour:two-factor-setup'
const TOUR_ID = 'two-factor-setup'
const cardSelector = '[data-two-factor-card]'
let activeTour = null

function stepContent(message, index, total, documentObject, showAppLinks = false) {
    const content = documentObject.createElement('div')
    content.append(buildTourProgressElement(index, total, documentObject))

    const body = documentObject.createElement('p')
    body.className = 'nurse-performance-tour-body'
    body.textContent = message
    content.append(body)

    if (showAppLinks) {
        const links = documentObject.createElement('p')
        links.className = 'nurse-performance-tour-body flex flex-wrap gap-x-4 gap-y-2'
        for (const [label, url] of [
            ['Get the iPhone or iPad app', 'https://apps.apple.com/us/app/google-authenticator/id388497605'],
            ['Get the Android app', 'https://play.google.com/store/apps/details?id=com.google.android.apps.authenticator2'],
        ]) {
            const link = documentObject.createElement('a')
            link.textContent = label
            link.className = 'text-primary-solid underline underline-offset-2 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary-solid'
            link.href = url
            link.target = '_blank'
            link.rel = 'noopener noreferrer'
            link.setAttribute('aria-label', `${label} (opens in a new tab)`)
            links.append(link)
        }
        content.append(links)
    }

    return content
}

export function startTwoFactorSetupTour(browserWindow, documentObject, ShepherdLibrary) {
    if (activeTour || ShepherdLibrary.activeTour || ! documentObject.querySelector(cardSelector)) {
        return false
    }

    const tour = new ShepherdLibrary.Tour({
        defaultStepOptions: {
            cancelIcon: {enabled: true},
            classes: 'nurse-performance-tour',
            scrollTo: {behavior: 'smooth', block: 'center'},
        },
        exitOnEsc: true,
        useModalOverlay: false,
    })
    activeTour = tour
    const deadline = documentObject.querySelector(cardSelector)?.getAttribute?.('data-two-factor-deadline') ?? 'your deadline'
    let observer = null

    const stopWatching = () => {
        observer?.disconnect()
        observer = null
    }

    const watchFor = (selector) => {
        stopWatching()
        const advance = () => {
            if (!documentObject.querySelector(selector)) return
            stopWatching()
            tour.next()
        }

        observer = new browserWindow.MutationObserver(advance)
        observer.observe(documentObject.body, {childList: true, subtree: true})
        advance()
    }

    const steps = [
        {
            id: 'two-factor-intro',
            selector: cardSelector,
            title: 'Set up two-step sign-in',
            message: `Set up two-step sign-in before ${deadline} to keep access to CPM. You need your phone and an app that shows 6-digit sign-in codes. Google Authenticator is free, and a compatible app you already use works too. You do not need to pay for an authenticator app; we recommend that you never pay for one.`,
            showAppLinks: true,
            buttons: [
                {action: () => tour.cancel(), classes: 'shepherd-button-secondary', text: 'Not now'},
                {action: () => tour.next(), text: 'Get started'},
            ],
        },
    ]

    if (documentObject.querySelector('[data-two-factor-enable]')) {
        steps.push({
            id: 'two-factor-enable',
            selector: '[data-two-factor-enable]',
            title: 'Turn on two-step sign-in',
            message: 'Select Start setup, then enter your CPM password when asked. Keep this page open while you set up the app on your phone.',
            buttons: [{action: () => tour.cancel(), classes: 'shepherd-button-secondary', text: 'Close guide'}],
            waitFor: '[data-two-factor-code]',
        })
    }

    if (documentObject.querySelector('[data-two-factor-continue]')) {
        steps.push({
            id: 'two-factor-continue',
            selector: '[data-two-factor-continue]',
            title: 'Continue your setup',
            message: 'You already started setup. Select Continue setup, then enter your CPM password when asked. The same square code appears again.',
            buttons: [{action: () => tour.cancel(), classes: 'shepherd-button-secondary', text: 'Close guide'}],
            waitFor: '[data-two-factor-code]',
        })
    }

    if (! documentObject.querySelector('[data-two-factor-show-recovery]')) {
        steps.push({
            id: 'two-factor-confirm',
            selector: cardSelector,
            title: 'Connect the code app on your phone',
            message: 'In your phone app, add an account and scan the square code. If you are on the same phone, enter the setup key instead. Type the 6-digit code from the app, then select Finish setup.',
            buttons: [{action: () => tour.cancel(), classes: 'shepherd-button-secondary', text: 'Close guide'}],
            waitFor: '[data-two-factor-recovery-codes]',
        })
    }

    if (documentObject.querySelector('[data-two-factor-show-recovery]')) {
        steps.push({
            id: 'two-factor-recovery',
            selector: '[data-two-factor-show-recovery]',
            title: 'Open your recovery codes',
            message: 'Select Show backup codes, then enter your password when asked. This guide reappears once you confirm.',
            buttons: [{action: () => tour.cancel(), classes: 'shepherd-button-secondary', text: 'Close guide'}],
            waitFor: '[data-two-factor-recovery-codes]',
        })
    }
    steps.push({
        id: 'two-factor-done',
        selector: cardSelector,
        title: 'Keep a way back in',
        message: 'Your account is protected. Save these backup codes somewhere secure that only you can access. Each works once if your phone is unavailable. On the same browser, CPM asks for another sign-in code only once every 24 hours.',
        buttons: [{action: () => tour.complete(), text: 'I saved them'}],
    })

    steps.forEach((step, index) => {
        tour.addStep({
            id: step.id,
            attachTo: {element: step.selector, on: 'bottom'},
            buttons: step.buttons,
            text: stepContent(step.message, index, steps.length, documentObject, step.showAppLinks),
            title: step.title,
            when: step.waitFor ? {show: () => watchFor(step.waitFor)} : undefined,
        })
    })

    const cleanup = () => {
        stopWatching()
        if (activeTour === tour) activeTour = null
    }
    tour.on('complete', () => {
        cleanup()
        postJson(browserWindow, `/tours/${TOUR_ID}/complete`)
    })
    tour.on('cancel', () => {
        cleanup()
        postJson(browserWindow, `/tours/${TOUR_ID}/dismiss`)
    })

    browserWindow.setTimeout(() => tour.start(), 200)

    return true
}

export function registerTwoFactorSetupTour(browserWindow, documentObject, ShepherdLibrary) {
    const start = () => startTwoFactorSetupTour(browserWindow, documentObject, ShepherdLibrary)
    browserWindow.addEventListener(TWO_FACTOR_SETUP_TOUR_EVENT, start)

    return () => browserWindow.removeEventListener(TWO_FACTOR_SETUP_TOUR_EVENT, start)
}
